import { HintAnimation } from '../../portal-hints/hint-motion';
import {
  abs,
  bubble,
  C,
  chip,
  glass,
  hline,
  icon,
  okChip,
  orbi,
  panel,
  pill,
  pressed,
  StoryScene,
  text,
  toggle,
} from './story-scene';

/**
 * Vorschau-Szenen der Essentials-Themen 1, 2 und 4 (PRD academy-content):
 * je Lektion EINE kurze Szene im Loop, die zeigt, worum es geht — und die
 * so auch als Portalhinweis-Film taugt. Gelernt wird im Klickpfad.
 * Begriffe wie im Portal (assets/i18n/de.json). Panel-Koordinaten ab
 * (20,18); der Zeiger rechnet in Bühnenkoordinaten (+20/+18).
 */

const hidden = (html: string) => html.replace('style="', 'style="opacity:0;');

// ── Thema 1 ─────────────────────────────────────────────────────────────

/** 1.1 — Menü, Seitenkopf mit Fragezeichen, Seitenhilfe öffnet sich. */
const portalTour = (): HintAnimation =>
  new StoryScene({
    seconds: 10,
    still: 8.5,
    html: panel(
      pressed(14, 24, 140, 36, 'nav0') +
        hidden(pressed(14, 66, 140, 36, 'nav1')) +
        hidden(pressed(14, 108, 140, 36, 'nav2')) +
        text(186, 26, 300, 'Wissensbasis', 20, 400) +
        text(186, 58, 300, 'Lege Ordner an und füge Inhalte hinzu.', 12, 500, C.muted) +
        `<div data-r="help" style="${abs(548, 26, 30, 30, `border-radius:999px;background:rgb(255 255 255 / .6);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px -3px rgb(38 65 112 / .3);`)}">${icon('help', 18, C.action)}</div>` +
        hline(180, 92, 404) +
        `<div data-r="pop" style="opacity:0;">` +
        glass(16, 0.85, 'left:330px;top:64px;width:250px;height:110px;', text(14, 12, 220, 'Seitenhilfe', 13, 600) + `<div style="${abs(14, 36, 222, 60, `font-size:12px;line-height:1.45;color:${C.text};white-space:normal;`)}">Hier steht ausführlich, was du auf dieser Seite tun kannst.</div>`) +
        `</div>`,
      1,
    ),
    tracks: [
      { r: 'nav0', at: [1.2, 1.5], o: [1, 0] },
      { r: 'nav1', at: [1.2, 1.5, 2.6, 2.9], o: [0, 1, 1, 0] },
      { r: 'nav2', at: [2.6, 2.9, 4.0, 4.3], o: [0, 1, 1, 0] },
      { r: 'pop', at: [5.6, 6.0], o: [0, 1], y: [-6, 0] },
    ],
    cursor: {
      at: [0, 1.1, 2.5, 3.9, 5.2, 10],
      x: [300, 110, 110, 110, 583, 583],
      y: [250, 60, 102, 144, 59, 59],
      clicks: [1.3, 2.7, 5.5],
    },
  });

/** 1.2 — Orbi öffnen, Frage stellen, Vorschlag freigeben. */
const portalAssistant = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10.5,
    html:
      glass(30, 0.42, 'left:20px;top:18px;width:600px;height:318px;', text(24, 22, 300, 'KI-Agenten', 20, 400) + hline(20, 60, 330)) +
      `<div style="${abs(560, 26, 34, 34)}">${orbi(0, 0, 34)}</div>` +
      `<div data-r="side" style="opacity:0;">` +
      glass(22, 0.7, 'left:380px;top:70px;width:236px;height:258px;', text(14, 12, 200, 'Orbi', 13, 600) + hline(12, 38, 212)) +
      `</div>` +
      hidden(bubble(430, 118, 180, 'Verknüpfe „Prüfungen“ mit dem Statistik-Tutor.', true, 'q')) +
      `<div data-r="prop" style="opacity:0;">` +
      glass(16, 0.9, 'left:392px;top:180px;width:212px;height:130px;', text(12, 10, 190, 'Vorschlag', 12, 600, C.muted) + `<div style="${abs(12, 32, 190, 40, `font-size:12.5px;line-height:1.4;color:${C.text};white-space:normal;`)}">Ordner „Prüfungen“ mit Statistik-Tutor verknüpfen</div>` + chip(12, 92, 84, 'Ablehnen') + pill(104, 88, 96, 'Freigeben', 'ok')) +
      `</div>` +
      hidden(okChip(496, 272, 104, 'Freigegeben', 'done')),
    tracks: [
      { r: 'side', at: [1.3, 1.8], o: [0, 1], x: [30, 0] },
      { r: 'q', at: [2.6, 3.0], o: [0, 1], y: [6, 0] },
      { r: 'prop', at: [4.4, 4.9], o: [0, 1], y: [8, 0] },
      { r: 'ok', at: [7.4, 7.7], o: [1, 0] },
      { r: 'done', at: [7.6, 8.0], o: [0, 1] },
    ],
    cursor: { at: [0, 1.0, 1.6, 6.6, 7.4, 12], x: [300, 577, 577, 544, 544, 544], y: [250, 43, 43, 284, 284, 284], clicks: [1.2, 7.2] },
  });

/** 1.3 — Portalhinweis unten links, „Mehr erfahren“ öffnet die Lektion. */
const hintsAndHelp = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html:
      glass(30, 0.42, 'left:20px;top:18px;width:600px;height:318px;', text(24, 22, 300, 'KI-Agenten', 20, 400) + hline(20, 60, 560)) +
      `<div data-r="hint" style="${abs(40, 150, 230, 160, `opacity:0;border-radius:16px;background:#243650;box-shadow:0 10px 24px -8px rgb(20 38 70 / .45);`)}">` +
      text(14, 12, 200, 'Erstelle eine Wissensbasis', 13, 600, '#fff') +
      `<div style="${abs(14, 38, 202, 64, 'border-radius:10px;background:linear-gradient(135deg,#dbe6f7,#f3e8ff);')}"></div>` +
      `<div style="${abs(14, 112, 76, 24, `border-radius:999px;background:${C.action};color:#fff;font-size:11px;font-weight:600;display:flex;align-items:center;justify-content:center;`)}">Gehe zu</div>` +
      `<div data-r="more" style="${abs(100, 116, 110, 18, 'font-size:11px;font-weight:500;color:rgb(255 255 255 / .85);text-decoration:underline;')}">Mehr erfahren</div>` +
      `</div>` +
      `<div data-r="lesson" style="opacity:0;">` +
      glass(20, 0.8, 'left:300px;top:84px;width:300px;height:226px;', text(16, 14, 260, 'Academy · Lektion', 11, 600, C.muted) + text(16, 34, 260, 'Eine Wissensbasis anlegen', 15, 600) + `<div style="${abs(16, 66, 268, 90, 'border-radius:12px;background:linear-gradient(135deg,#dbe6f7,#f3e8ff);')}"></div>` + pill(16, 176, 160, 'Klickpfad starten')) +
      `</div>`,
    tracks: [
      { r: 'hint', at: [0.8, 1.3], o: [0, 1], y: [16, 0] },
      { r: 'lesson', at: [4.6, 5.1], o: [0, 1], x: [24, 0] },
    ],
    cursor: { at: [0, 2.8, 4.2, 6.6, 11], x: [380, 172, 172, 396, 396], y: [300, 275, 275, 276, 276], clicks: [4.0, 6.9] },
  });

// ── Thema 2 ─────────────────────────────────────────────────────────────

const templateTile = (x: number, ic: string, name: string, n: number, sel = false) =>
  `<div data-r="tpl${n}">` +
  (sel ? hidden(pressed(x - 4, 122, 128, 92, 'tplSel')) : '') +
  glass(16, 0.55, `left:${x}px;top:126px;width:120px;height:84px;`, `<div style="${abs(48, 12, 24, 24)}">${icon(ic, 24, C.action)}</div>` + text(8, 46, 104, name, 12.5, 600, C.text, 'text-align:center;')) +
  `</div>`;

/** 2.1 — Hinzufügen, Vorlage wählen, Name, Erstellen → neue Kachel. */
const createAgent = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10.5,
    html:
      glass(30, 0.42, 'left:20px;top:18px;width:600px;height:318px;', text(24, 22, 300, 'KI-Agenten', 20, 400) + pill(470, 22, 110, 'Hinzufügen', 'add') + hline(20, 64, 560)) +
      `<div data-r="wiz" style="opacity:0;">` +
      glass(22, 0.85, 'left:90px;top:70px;width:460px;height:250px;', text(20, 14, 300, 'Neuer KI-Agent', 15, 600) + hline(20, 42, 420)) +
      templateTile(110, 'book', 'Alpha Tutor', 1, true) +
      templateTile(250, 'users', 'Alpha Twin', 2) +
      templateTile(390, 'sliders', 'Alpha Flexible', 3) +
      `<div data-r="name" style="opacity:0;">` +
      `<div style="${abs(110, 232, 290, 34, `border-radius:999px;background:rgb(255 255 255 / .6);display:flex;align-items:center;padding:0 14px;box-sizing:border-box;font-size:13px;color:${C.text};`)}">Statistik-Tutor</div>` +
      pill(412, 233, 116, 'Erstellen', 'create') +
      `</div></div>` +
      `<div data-r="tile" style="opacity:0;">` +
      glass(20, 0.6, 'left:40px;top:96px;width:200px;height:120px;', orbi(14, 14, 36) + text(58, 18, 130, 'Statistik-Tutor', 14, 600) + text(58, 38, 130, 'Alpha Tutor', 11.5, 500, C.muted)) +
      `</div>`,
    tracks: [
      { r: 'wiz', at: [1.4, 1.8, 8.0, 8.4], o: [0, 1, 1, 0], s: [0.96, 1, 1, 1] },
      { r: 'tplSel', at: [3.2, 3.5], o: [0, 1] },
      { r: 'name', at: [4.4, 4.8], o: [0, 1] },
      { r: 'tile', at: [8.4, 8.9], o: [0, 1], y: [10, 0] },
    ],
    cursor: { at: [0, 1.0, 1.6, 2.8, 3.6, 6.8, 7.6, 12], x: [300, 545, 545, 170, 170, 470, 470, 470], y: [250, 56, 56, 168, 168, 249, 249, 249], clicks: [1.2, 3.2, 7.4] },
  });

/** 2.2 — Die vier Vorlagen mit ihrem Einsatz. */
const agentTemplates = (): HintAnimation => {
  const col = (x: number, ic: string, name: string, use: string, n: number) =>
    `<div data-r="c${n}" style="opacity:0;">` +
    glass(18, 0.55, `left:${x}px;top:70px;width:128px;height:220px;`, `<div style="${abs(52, 18, 24, 24)}">${icon(ic, 24, C.action)}</div>` + text(8, 56, 112, name, 13, 600, C.text, 'text-align:center;') + hline(14, 84, 100) + `<div style="${abs(12, 96, 104, 110, `font-size:11.5px;line-height:1.45;color:${C.text};white-space:normal;text-align:center;`)}">${use}</div>`) +
    `</div>`;
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(40, 22, 520, 'Welche Vorlage passt?', 15, 600) +
        col(26, 'book', 'Alpha Tutor', 'Begleitet Lernende fachlich durch einen Kurs.', 1) +
        col(166, 'users', 'Alpha Twin', 'Spricht im Namen einer Lehrperson.', 2) +
        col(306, 'help', 'Alpha Support', 'Beantwortet Organisatorisches.', 3) +
        col(446, 'sliders', 'Alpha Flexible', 'Frei gestaltbar: Persona, Stil, Ziele, Grenzen.', 4),
    ),
    tracks: [1, 2, 3, 4].map((n) => ({ r: `c${n}`, at: [0.8 + (n - 1) * 1.6, 1.3 + (n - 1) * 1.6], o: [0, 1], y: [10, 0] })),
  });
};

/** 2.3 — Persona schreiben, „Formell“ einschalten, Vorschau siezt. */
const aiConfiguration = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10.5,
    html: panel(
      text(184, 22, 300, 'Basis & Persona', 15, 600) +
        text(184, 56, 180, 'Persona', 12, 600, C.muted) +
        `<div style="${abs(184, 76, 230, 70, `border-radius:14px;background:rgb(255 255 255 / .55);box-shadow:inset 0 3px 6px -3px rgb(47 76 121 / .23);`)}">` +
        `<div data-r="p1" style="${abs(12, 10, 206, 18, `opacity:0;font-size:12px;color:${C.text};`)}">Freundlicher Statistik-Coach,</div>` +
        `<div data-r="p2" style="${abs(12, 28, 206, 18, `opacity:0;font-size:12px;color:${C.text};`)}">erklärt mit Beispielen aus dem</div>` +
        `<div data-r="p3" style="${abs(12, 46, 206, 18, `opacity:0;font-size:12px;color:${C.text};`)}">Alltag.</div></div>` +
        text(184, 166, 180, 'Formell', 12, 600, C.muted) +
        toggle(376, 164, 'f') +
        hline(180, 204, 240) +
        glass(18, 0.6, 'left:430px;top:20px;width:156px;height:278px;', orbi(10, 10, 22) + text(38, 12, 110, 'Vorschau', 12, 600) + hline(8, 40, 140)) +
        `<div data-r="du">${bubble(442, 80, 134, 'Wie kann ich dir helfen?', false)}</div>` +
        `<div data-r="sie" style="opacity:0;">${bubble(442, 80, 134, 'Wie kann ich Ihnen helfen?', false)}</div>`,
      null,
    ),
    tracks: [
      { r: 'p1', at: [1.0, 1.4], o: [0, 1] },
      { r: 'p2', at: [1.8, 2.2], o: [0, 1] },
      { r: 'p3', at: [2.6, 3.0], o: [0, 1] },
      { r: 'f', at: [5.0, 5.2], o: [0, 1] },
      { r: 'fKnob', at: [5.0, 5.2], x: [0, 16] },
      { r: 'du', at: [5.8, 6.2], o: [1, 0] },
      { r: 'sie', at: [5.8, 6.2], o: [0, 1] },
    ],
    cursor: { at: [0, 0.8, 3.2, 4.4, 5.4, 12], x: [300, 300, 300, 415, 415, 415], y: [250, 140, 140, 193, 193, 193], clicks: [4.8] },
  });

/** 2.4 — Play an der Kachel, Vorschau-Chat antwortet mit Quelle. */
const tryAgent = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html:
      glass(30, 0.42, 'left:20px;top:18px;width:600px;height:318px;', text(24, 22, 300, 'KI-Agenten', 20, 400) + hline(20, 60, 560)) +
      glass(20, 0.6, 'left:44px;top:94px;width:220px;height:150px;', orbi(14, 14, 36) + text(58, 18, 150, 'Statistik-Tutor', 14, 600) + text(58, 38, 150, 'Alpha Tutor', 11.5, 500, C.muted)) +
      `<div data-r="play" style="${abs(70, 206, 30, 30, `border-radius:999px;background:${C.action};display:flex;align-items:center;justify-content:center;transform-origin:50% 50%;`)}"><svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 1.5v9l7-4.5z" fill="#fff"/></svg></div>` +
      `<div data-r="chat" style="opacity:0;">` +
      glass(22, 0.75, 'left:300px;top:70px;width:300px;height:250px;', orbi(12, 10, 24) + text(44, 12, 200, 'Statistik-Tutor', 13, 600) + hline(10, 42, 280)) +
      `</div>` +
      hidden(bubble(400, 128, 190, 'Was ist die Varianz?', true, 'q')) +
      hidden(bubble(312, 172, 260, 'Die mittlere quadrierte Abweichung vom Mittelwert.', false, 'a')) +
      hidden(okChip(312, 228, 200, 'Quelle: Skript Kapitel 3', 'src')),
    tracks: [
      { r: 'play', at: [1.5, 1.6, 1.8], s: [1, 0.9, 1] },
      { r: 'chat', at: [2.0, 2.5], o: [0, 1], x: [24, 0] },
      { r: 'q', at: [3.4, 3.8], o: [0, 1], y: [6, 0] },
      { r: 'a', at: [5.2, 5.6], o: [0, 1], y: [6, 0] },
      { r: 'src', at: [6.2, 6.6], o: [0, 1] },
    ],
    cursor: { at: [0, 1.2, 2.2, 11], x: [300, 85, 85, 85], y: [260, 221, 221, 221], clicks: [1.5] },
  });

// ── Thema 4 ─────────────────────────────────────────────────────────────

/** Kleines Chatfenster-Gerüst (Vorschau rechts). */
const chatWindow = (x: number, y: number, head: string, h = 250) =>
  `<div style="${abs(x, y, 210, h)}">` +
  `<div style="${abs(0, 0, 210, h, 'border-radius:18px;background:#fff;box-shadow:0 10px 24px -12px rgb(38 65 112 / .4);overflow:hidden;')}">` +
  `<div style="${abs(0, 0, 210, 44, `background:${head};`)}"></div>` +
  `</div></div>`;

/** 4.1 — Schalter Liquid | Chat, die Vorschau wechselt. */
const widgetInterface = (): HintAnimation =>
  new StoryScene({
    seconds: 10,
    still: 8.5,
    html: panel(
      text(30, 22, 200, 'Interface', 20, 400) +
        `<div style="${abs(200, 22, 160, 32, 'border-radius:999px;background:rgb(255 255 255 / .5);box-shadow:inset 0 3px 6px -3px rgb(47 76 121 / .23);')}"></div>` +
        `<div data-r="selL" style="${abs(203, 25, 77, 26, 'border-radius:999px;background:#fff;box-shadow:0 2px 6px -3px rgb(38 65 112 / .35);')}"></div>` +
        text(203, 30, 77, 'Liquid', 13, 600, C.text, 'text-align:center;') +
        text(280, 30, 77, 'Chat', 13, 600, C.text, 'text-align:center;') +
        hline(20, 70, 560) +
        // Liquid: Orbi + Eingabeleiste
        `<div data-r="liquid">${orbi(150, 210, 42)}<div style="${abs(200, 214, 280, 36, `border-radius:999px;background:rgb(255 255 255 / .75);box-shadow:0 6px 14px -8px rgb(38 65 112 / .45);display:flex;align-items:center;padding:0 14px;box-sizing:border-box;font-size:12.5px;color:${C.muted};`)}">Frag mich etwas …</div></div>` +
        // Chat: Fenster
        `<div data-r="win" style="opacity:0;">${chatWindow(196, 82, 'linear-gradient(90deg,#003598,#3B82F6)', 222)}${bubble(206, 140, 150, 'Hallo! Wie kann ich helfen?', false)}</div>`,
    ),
    tracks: [
      { r: 'selL', at: [3.0, 3.4], x: [0, 77] },
      { r: 'liquid', at: [3.2, 3.6], o: [1, 0] },
      { r: 'win', at: [3.4, 3.8], o: [0, 1], y: [10, 0] },
    ],
    cursor: { at: [0, 2.4, 3.6, 10], x: [300, 338, 338, 338], y: [250, 66, 66, 66], clicks: [3.0] },
  });

/** 4.2 — Farbe wählen, Logo erscheint, Fenster nimmt die CI an. */
const widgetStyling = (): HintAnimation => {
  const sw = (x: number, c: string) => `<div style="${abs(x, 0, 26, 26, `border-radius:999px;background:${c};box-shadow:0 0 0 2px #fff, 0 2px 6px -2px rgb(38 65 112 / .4);`)}"></div>`;
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(30, 22, 200, 'Interface', 20, 400) +
        hline(20, 64, 560) +
        text(30, 84, 200, 'Primärfarbe', 12, 600, C.muted) +
        `<div style="${abs(30, 106, 140, 26)}">${sw(0, '#003598')}${sw(36, '#0f766e')}${sw(72, '#9333ea')}</div>` +
        text(30, 152, 200, 'Logo', 12, 600, C.muted) +
        `<div style="${abs(30, 174, 150, 34, `border-radius:999px;background:rgb(255 255 255 / .55);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:${C.action};`)}">Logo hochladen</div>` +
        chatWindow(330, 50, 'linear-gradient(90deg,#003598,#3B82F6)') +
        `<div data-r="teal" style="opacity:0;">${chatWindow(330, 50, 'linear-gradient(90deg,#0f766e,#14b8a6)')}</div>` +
        `<div data-r="logo" style="${abs(346, 62, 180, 20, 'opacity:0;font-size:13px;font-weight:700;color:#fff;letter-spacing:.02em;white-space:nowrap;')}">Hochschule Nord</div>` +
        bubble(342, 110, 150, 'Willkommen! Wie kann ich helfen?', false),
    ),
    tracks: [
      { r: 'teal', at: [2.6, 3.0], o: [0, 1] },
      { r: 'logo', at: [6.0, 6.4], o: [0, 1] },
    ],
    cursor: { at: [0, 2.0, 3.0, 5.0, 6.2, 11], x: [300, 99, 99, 125, 125, 125], y: [260, 137, 137, 209, 209, 209], clicks: [2.4, 5.6] },
  });
};

/** 4.3 — Antwort mit Karteikarte, Häufigkeit einstellen. */
const learningAids = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      bubble(40, 30, 300, 'Hier sind die wichtigsten Begriffe zum Üben:', false) +
        `<div data-r="card" style="opacity:0;">` +
        glass(16, 0.85, 'left:40px;top:78px;width:300px;height:140px;', text(16, 14, 200, 'Karteikarte 1 / 3', 11, 600, C.muted) + `<div style="${abs(16, 44, 268, 50, `font-size:17px;font-weight:600;color:${C.text};white-space:normal;text-align:center;line-height:1.3;`)}">Was misst die Standardabweichung?</div>` + pill(100, 98, 100, 'Umdrehen')) +
        `</div>` +
        `<div data-r="cfg" style="opacity:0;">` +
        glass(18, 0.6, 'left:370px;top:70px;width:210px;height:170px;', text(14, 12, 180, 'Lernhilfen', 13, 600) + hline(12, 38, 186) + text(14, 52, 180, 'Häufigkeit', 12, 600, C.muted) + chip(14, 76, 82, 'Vereinzelt') + okChip(102, 76, 94, 'Ausgewogen') + chip(14, 108, 82, 'Häufig')) +
        `</div>`,
    ),
    tracks: [
      { r: 'card', at: [1.2, 1.7], o: [0, 1], s: [0.94, 1] },
      { r: 'cfg', at: [5.0, 5.5], o: [0, 1], x: [20, 0] },
    ],
  });

/** 4.4 — KI-Hinweis bestätigen, dann erklärt die Tour den Chat. */
const onboardingTour = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      chatWindow(190, 30, 'linear-gradient(90deg,#003598,#3B82F6)') +
        `<div data-r="notice">` +
        glass(14, 0.95, 'left:204px;top:92px;width:182px;height:110px;', text(12, 10, 160, 'KI-Hinweis', 12, 600) + `<div style="${abs(12, 32, 160, 40, `font-size:11.5px;line-height:1.4;color:${C.text};white-space:normal;`)}">Du chattest mit einer KI.</div>` + pill(12, 70, 100, 'Verstanden', 'ok')) +
        `</div>` +
        `<div style="${abs(200, 236, 190, 32, `border-radius:999px;background:rgb(255 255 255 / .8);box-shadow:0 2px 6px -3px rgb(38 65 112 / .3);display:flex;align-items:center;padding:0 12px;box-sizing:border-box;font-size:12px;color:${C.muted};`)}">Frag mich etwas …</div>` +
        `<div data-r="tip" style="opacity:0;">` +
        `<div style="${abs(214, 168, 170, 56, 'border-radius:12px;background:#243650;box-shadow:0 10px 24px -8px rgb(20 38 70 / .45);')}">${text(12, 8, 150, 'Hier stellst du', 12, 600, '#fff')}${text(12, 28, 150, 'deine Frage.', 12, 600, '#fff')}</div>` +
        `<div style="${abs(198, 232, 194, 40, 'border-radius:999px;box-shadow:0 0 0 3px rgb(59 130 246 / .55);')}"></div>` +
        `</div>`,
    ),
    tracks: [
      { r: 'notice', at: [3.0, 3.4], o: [1, 0], s: [1, 0.96] },
      { r: 'tip', at: [4.2, 4.7], o: [0, 1], y: [6, 0] },
    ],
    cursor: { at: [0, 1.6, 3.2, 11], x: [420, 286, 286, 286], y: [300, 198, 198, 198], clicks: [2.8] },
  });

/** Szenen der Essentials-Themen 1, 2 und 4, je ID (Katalog `video.scenes`). */
export const ESSENTIALS_SCENES = {
  'portal-tour': portalTour,
  'portal-assistant': portalAssistant,
  'hints-and-help': hintsAndHelp,
  'create-agent': createAgent,
  'agent-templates': agentTemplates,
  'ai-configuration': aiConfiguration,
  'try-agent': tryAgent,
  'widget-interface': widgetInterface,
  'widget-styling': widgetStyling,
  'learning-aids': learningAids,
  'onboarding-tour': onboardingTour,
} satisfies Record<string, () => HintAnimation>;
